import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Alert,
  Spinner,
  Button,
  Input,
  Label,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import api from "../helpers/api";
import { uploadServiceContractFiles, validateFiles } from "../helpers/fileUpload";

const toIsoDate = (d) => {
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const emptyForm = {
  contractNumber: "",
  contractCategoryId: "",
  vendorId: "",
  assetIds: [],
  contractValue: "",
  startDate: "",
  endDate: "",
  notes: "",
  attachmentUrls: [],
};

const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    borderColor: hasError ? "#dc3545" : state.isFocused ? "#86b7fe" : "#ced4da",
    boxShadow: hasError
      ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
      : state.isFocused
      ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
      : "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#3980c0" : state.isFocused ? "#3980c0" : "white",
    color: state.isSelected || state.isFocused ? "white" : "#212529",
    "&:active": { backgroundColor: "#2e669a" },
  }),
  multiValue: (base) => ({ ...base, backgroundColor: "#3980c0", borderRadius: "4px" }),
  multiValueLabel: (base) => ({ ...base, color: "white" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "white",
    ":hover": { backgroundColor: "#2e669a", color: "white" },
  }),
});

const ContractFormModal = ({ isOpen, toggle, contract, categories, onSaved }) => {
  const { t } = useTranslation();

  const authUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("authUser") || "{}");
    } catch {
      return {};
    }
  })();
  const organizationId = authUser?.activeOrganizationId ?? null;
  // One currency per org (no FX conversion) — always the org's current currency, read from
  // the canonical active-context slice. See currency-uniformity work.
  const orgCurrency =
    useSelector((s) => s.ActiveContext?.activeOrganizationCurrency) ||
    authUser?.activeOrganizationCurrency ||
    "USD";

  const [form, setForm] = useState(emptyForm);
  const [vendors, setVendors] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const assetDebounceRef = useRef(null);

  const fetchAssets = useCallback(
    async (search = "") => {
      setAssetSearchLoading(true);
      try {
        const params = { count: 50, hierarchy: false };
        if (organizationId) params.organizationId = organizationId;
        if (search && search.trim()) params.search = search.trim();
        const { data } = await api.get("asset/all", { params });
        const raw = data?.response;
        const assets = Array.isArray(raw)
          ? raw[0]?.assets || raw
          : raw?.assets || raw?.content || [];
        setAssetOptions(
          assets.map((a) => ({
            value: a.id,
            label: `${a.assetName || a.name || ""}${a.assetNumber ? ` · ${a.assetNumber}` : ""}`,
            raw: a,
          }))
        );
      } catch (e) {
        setAssetOptions([]);
      } finally {
        setAssetSearchLoading(false);
      }
    },
    [organizationId]
  );

  const fetchVendors = useCallback(async () => {
    try {
      const { data } = await api.get("vendor/getAllVendors");
      setVendors(data?.response || []);
    } catch (e) {
      setVendors([]);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setApiError("");
    setErrors({});
    if (contract?.id) {
      setForm({
        id: contract.id,
        contractNumber: contract.contractNumber || "",
        contractCategoryId: contract.contractCategoryId || "",
        vendorId: contract.vendorId || "",
        assetIds: contract.assetIds || (contract.assets || []).map((a) => a.id),
        contractValue: contract.contractValue ?? "",
        startDate: contract.startDate || "",
        endDate: contract.endDate || "",
        notes: contract.notes || "",
        attachmentUrls: contract.attachmentUrls || [],
      });
      // Seed asset options with the currently linked assets so the picker can render them immediately.
      const linked = (contract.assets || []).map((a) => ({
        value: a.id,
        label: `${a.name || ""}${a.assetNumber ? ` · ${a.assetNumber}` : ""}`,
      }));
      setAssetOptions((prev) => {
        const byId = new Map(prev.map((o) => [o.value, o]));
        linked.forEach((o) => byId.set(o.value, o));
        return Array.from(byId.values());
      });
    } else {
      setForm(emptyForm);
    }
    fetchAssets("");
    fetchVendors();
  }, [isOpen, contract, fetchAssets, fetchVendors]);

  const handleAssetSearch = (input) => {
    if (assetDebounceRef.current) clearTimeout(assetDebounceRef.current);
    assetDebounceRef.current = setTimeout(() => fetchAssets(input), 300);
  };

  const categoryOptions = useMemo(
    () =>
      (categories || []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [categories]
  );

  const vendorOptions = useMemo(
    () =>
      (vendors || []).map((v) => ({
        value: v.id,
        label: v.vendorName || v.name,
      })),
    [vendors]
  );

  const selectedAssetOptions = useMemo(() => {
    const ids = new Set(form.assetIds || []);
    return assetOptions.filter((o) => ids.has(o.value));
  }, [assetOptions, form.assetIds]);

  const validate = () => {
    const e = {};
    if (!form.contractNumber.trim())
      e.contractNumber = t("contracts.errors.number_required", "Contract number is required");
    if (!form.contractCategoryId)
      e.contractCategoryId = t("contracts.errors.category_required", "Category is required");
    if (form.contractValue === "" || form.contractValue == null)
      e.contractValue = t("contracts.errors.value_required", "Contract value is required");
    else if (Number(form.contractValue) < 0)
      e.contractValue = t("contracts.errors.value_negative", "Value must be 0 or greater");
    if (!form.startDate) e.startDate = t("contracts.errors.start_required", "Start date is required");
    if (!form.endDate) e.endDate = t("contracts.errors.end_required", "End date is required");
    if (form.startDate && form.endDate && form.endDate < form.startDate)
      e.endDate = t("contracts.errors.end_before_start", "End date cannot be before start date");
    if (!form.assetIds || form.assetIds.length === 0)
      e.assetIds = t("contracts.errors.assets_required", "At least one asset is required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    setApiError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        contractCategoryId: Number(form.contractCategoryId),
        vendorId: form.vendorId ? Number(form.vendorId) : null,
        contractValue: Number(form.contractValue),
      };
      if (form.id) {
        await api.put("contract/update", payload);
      } else {
        await api.post("contract/create", payload);
      }
      onSaved && onSaved();
    } catch (err) {
      setApiError(
        err?.response?.data?.message || t("shared.error_submission", "An error occurred during submission")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const validation = validateFiles(files);
    if (validation.validFiles.length === 0) {
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const urls = await uploadServiceContractFiles(validation.validFiles);
      setForm((prev) => ({ ...prev, attachmentUrls: [...(prev.attachmentUrls || []), ...urls] }));
    } catch (err) {
      setApiError(t("shared.failed_upload", "Failed to upload files. Please try again."));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (idx) => {
    setForm((prev) => ({
      ...prev,
      attachmentUrls: (prev.attachmentUrls || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {form.id ? t("contracts.edit_contract", "Edit Contract") : t("contracts.add_contract", "Add Contract")}
      </ModalHeader>
      <ModalBody>
        <Row className="g-3">
          {/* Contract Number — full width */}
          <Col md={12}>
            <Label className="form-label fw-medium">
              {t("contracts.contract_number_full", "Contract Number")} <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              value={form.contractNumber}
              onChange={(e) => setForm({ ...form, contractNumber: e.target.value })}
              className={errors.contractNumber ? "is-invalid" : ""}
            />
            {errors.contractNumber && <small className="text-danger">{errors.contractNumber}</small>}
          </Col>

          {/* Category — half width */}
          <Col md={6}>
            <Label className="form-label fw-medium">
              {t("contracts.category", "Category")} <span className="text-danger">*</span>
            </Label>
            <Select
              options={categoryOptions}
              value={categoryOptions.find((o) => o.value === Number(form.contractCategoryId)) || null}
              onChange={(opt) => setForm({ ...form, contractCategoryId: opt?.value || "" })}
              isClearable
              isSearchable
              placeholder={t("contracts.select_category", "Select category")}
              classNamePrefix="react-select"
              styles={selectStyles(!!errors.contractCategoryId)}
            />
            {errors.contractCategoryId && <small className="text-danger">{errors.contractCategoryId}</small>}
          </Col>

          {/* Contract Value — half width with currency badge */}
          <Col md={6}>
            <Label className="form-label fw-medium">
              {t("contracts.contract_value", "Contract Value")} <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type="number"
                step="0.01"
                value={form.contractValue}
                onChange={(e) => setForm({ ...form, contractValue: e.target.value })}
                className={errors.contractValue ? "is-invalid" : ""}
              />
              <InputGroupText>{orgCurrency}</InputGroupText>
            </InputGroup>
            {errors.contractValue && <small className="text-danger">{errors.contractValue}</small>}
          </Col>

          {/* Assets — full width searchable multi-select */}
          <Col md={12}>
            <Label className="form-label fw-medium">
              {t("contracts.assets", "Assets")} <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              isSearchable
              isLoading={assetSearchLoading}
              options={assetOptions}
              value={selectedAssetOptions}
              onInputChange={handleAssetSearch}
              onChange={(opts) => setForm({ ...form, assetIds: (opts || []).map((o) => o.value) })}
              classNamePrefix="react-select"
              styles={selectStyles(!!errors.assetIds)}
              placeholder={t("contracts.search_assets", "Type to search assets...")}
              noOptionsMessage={() =>
                assetSearchLoading
                  ? t("shared.loading", "Loading…")
                  : t("contracts.no_assets_found", "No assets found")
              }
              filterOption={null}
            />
            {errors.assetIds && <small className="text-danger">{errors.assetIds}</small>}
          </Col>

          {/* Start Date / End Date — half each */}
          <Col md={6}>
            <Label className="form-label fw-medium">
              {t("contracts.start_date", "Start Date")} <span className="text-danger">*</span>
            </Label>
            <DatePicker
              selected={form.startDate ? new Date(form.startDate) : null}
              onChange={(date) => setForm({ ...form, startDate: toIsoDate(date) })}
              dateFormat="dd/MM/yyyy"
              className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
              wrapperClassName="w-100"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              placeholderText={t("shared.dd_mm_yyyy", "dd/mm/yyyy")}
              isClearable
            />
            {errors.startDate && <div className="text-danger small mt-1">{errors.startDate}</div>}
          </Col>
          <Col md={6}>
            <Label className="form-label fw-medium">
              {t("contracts.end_date", "End Date")} <span className="text-danger">*</span>
            </Label>
            <DatePicker
              selected={form.endDate ? new Date(form.endDate) : null}
              onChange={(date) => setForm({ ...form, endDate: toIsoDate(date) })}
              dateFormat="dd/MM/yyyy"
              className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
              wrapperClassName="w-100"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              placeholderText={t("shared.dd_mm_yyyy", "dd/mm/yyyy")}
              isClearable
            />
            {errors.endDate && <div className="text-danger small mt-1">{errors.endDate}</div>}
            <div className="text-muted small mt-1">
              <i className="bx bx-bell me-1"></i>
              {t("contracts.reminder_schedule_hint", "Admins are notified 90/60/30/7 days before and on the end date.")}
            </div>
          </Col>

          {/* Vendor — full width */}
          <Col md={12}>
            <Label className="form-label fw-medium">{t("contracts.vendor", "Vendor")}</Label>
            <Select
              options={vendorOptions}
              value={vendorOptions.find((o) => o.value === Number(form.vendorId)) || null}
              onChange={(opt) => setForm({ ...form, vendorId: opt?.value || "" })}
              isClearable
              isSearchable
              placeholder={t("contracts.select_vendor", "Select vendor")}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
            />
          </Col>

          {/* Attachments — full width, dashed border card */}
          <Col md={12}>
            <Label className="form-label fw-medium">{t("shared.files", "Files")}</Label>
            <div
              className="border rounded p-4 text-center"
              style={{
                borderStyle: "dashed",
                borderWidth: "2px",
                borderColor: "#dee2e6",
                backgroundColor: "#fafbfc",
              }}
            >
              <input
                type="file"
                id="contractFileInput"
                className="d-none"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                disabled={uploading}
                onChange={handleFileChange}
              />
              <label
                htmlFor="contractFileInput"
                className={`btn btn-outline-secondary mb-2 ${uploading ? "disabled" : ""}`}
                style={{ cursor: uploading ? "not-allowed" : "pointer" }}
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t("shared.uploading", "Uploading...")}
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="paperclip" size={16} className="me-2" />
                    {t("shared.attach_files", "Attach Files")}
                  </>
                )}
              </label>
              <p className="text-muted small mb-0">
                {uploading
                  ? t("shared.wait_uploading_files", "Please wait while files are being uploaded...")
                  : t("shared.click_to_select_files", "Click to select files to attach")}
              </p>
            </div>
            {(form.attachmentUrls || []).length > 0 && (
              <div className="mt-3">
                {form.attachmentUrls.map((url, idx) => (
                  <div
                    key={idx}
                    className="d-flex align-items-center justify-content-between bg-light rounded px-3 py-2 mb-2"
                  >
                    <div className="d-flex align-items-center">
                      <FeatherIcon icon="file-text" size={18} className="text-primary me-2" />
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-truncate"
                        style={{ maxWidth: "300px" }}
                      >
                        {decodeURIComponent(url.split("/").pop() || `file-${idx + 1}`)}
                      </a>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeAttachment(idx)}
                    >
                      <FeatherIcon icon="trash-2" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Col>

          {/* Notes — at the bottom */}
          <Col md={12}>
            <Label className="form-label fw-medium">{t("contracts.notes", "Notes")}</Label>
            <Input
              type="textarea"
              rows={3}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Col>
        </Row>
        {apiError && <Alert color="danger" className="mt-3">{apiError}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" outline onClick={toggle} disabled={submitting}>
          {t("shared.cancel", "Cancel")}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? t("shared.processing", "Processing...")
            : form.id
            ? t("contracts.update_contract", "Update Contract")
            : t("contracts.create_contract", "Create Contract")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ContractFormModal;
